/**
 * Anmeldung und erzwungener Passwortwechsel.
 */

import { useEffect, useState } from 'react';

import { ApiError, api } from '../api/client';
import { useAuth } from '../auth';
import { Alert, Field, TextInput } from '../components/ui';
import { useToast } from '../components/toast';

export function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Notfallzugang: nur anbieten, wenn die Installation einen Schluessel hat.
  const [recoveryAvailable, setRecoveryAvailable] = useState(false);
  const [recovering, setRecovering] = useState(false);
  useEffect(() => {
    api
      .get<{ available: boolean }>('/api/auth/recovery-available')
      .then((result) => setRecoveryAvailable(result.available))
      .catch(() => setRecoveryAvailable(false));
  }, []);

  if (recovering) {
    return (
      <RecoveryPage
        onDone={(name) => {
          setRecovering(false);
          setUsername(name);
          setPassword('');
          setError(null);
        }}
        onCancel={() => setRecovering(false)}
      />
    );
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await login(username, password);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Die Anmeldung ist fehlgeschlagen.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="login-title">
          <span className="brand-mark">C</span>
          <div>
            <h1>Canalis</h1>
            <div className="faint" style={{ fontSize: 12.5 }}>
              Warenwirtschaft
            </div>
          </div>
        </div>

        {error ? <Alert kind="error">{error}</Alert> : null}

        <div className="stack" style={{ gap: 14 }}>
          <Field label="Benutzername" htmlFor="username" required>
            <TextInput
              id="username"
              value={username}
              onChange={setUsername}
              autoFocus
              autoComplete="username"
            />
          </Field>

          <Field label="Passwort" htmlFor="password" required>
            <TextInput
              id="password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
            />
          </Field>

          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Wird geprüft …' : 'Anmelden'}
          </button>

          {recoveryAvailable ? (
            <button
              type="button"
              className="btn-link faint"
              style={{ fontSize: 12.5, alignSelf: 'center', background: 'none', border: 0, cursor: 'pointer' }}
              onClick={() => setRecovering(true)}
            >
              Zugang wiederherstellen …
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}

/**
 * Notfallzugang: Mit dem Wiederherstellungsschluessel aus dem Passwortmanager
 * bekommt ein Administrator ein neues Passwort. Der Schluessel meldet nicht an
 * und umgeht nichts - danach geht es zurueck zur normalen Anmeldung.
 */
function RecoveryPage({ onDone, onCancel }: { onDone: (username: string) => void; onCancel: () => void }) {
  const toast = useToast();
  const [username, setUsername] = useState('');
  const [recoveryKey, setRecoveryKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrors({});
    if (newPassword !== repeatPassword) {
      setErrors({ repeatPassword: 'Die Passwörter stimmen nicht überein.' });
      return;
    }
    setBusy(true);
    try {
      const result = await api.post<{ ok: true; username: string }>('/api/auth/recover', {
        username,
        recoveryKey,
        newPassword,
      });
      toast.success('Das Passwort wurde neu gesetzt. Bitte melden Sie sich jetzt an.');
      onDone(result.username);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setErrors({ _: caught.message, ...caught.fieldErrors });
      } else {
        setErrors({ _: 'Die Wiederherstellung ist fehlgeschlagen.' });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="login-title">
          <span className="brand-mark">C</span>
          <div>
            <h1>Zugang wiederherstellen</h1>
            <div className="faint" style={{ fontSize: 12.5 }}>
              Neues Passwort für einen Administrator per Wiederherstellungsschlüssel
            </div>
          </div>
        </div>

        {errors._ ? <Alert kind="error">{errors._}</Alert> : null}

        <Alert kind="info">
          Der Schlüssel liegt im Passwortmanager der Geschäftsführung. Jede Nutzung wird
          protokolliert und allen Administratoren gemeldet.
        </Alert>

        <div className="stack" style={{ gap: 14 }}>
          <Field label="Benutzername des Administrators" htmlFor="rec-username" required>
            <TextInput id="rec-username" value={username} onChange={setUsername} autoFocus autoComplete="username" />
          </Field>
          <Field label="Wiederherstellungsschlüssel" htmlFor="rec-key" required error={errors.recoveryKey}>
            <TextInput
              id="rec-key"
              type="password"
              value={recoveryKey}
              onChange={setRecoveryKey}
              autoComplete="one-time-code"
              placeholder="XXXXXX-XXXXXX-XXXXXX-…"
            />
          </Field>
          <Field label="Neues Passwort" htmlFor="rec-new" required error={errors.newPassword}>
            <TextInput id="rec-new" type="password" value={newPassword} onChange={setNewPassword} autoComplete="new-password" />
          </Field>
          <Field label="Neues Passwort wiederholen" htmlFor="rec-repeat" required error={errors.repeatPassword}>
            <TextInput id="rec-repeat" type="password" value={repeatPassword} onChange={setRepeatPassword} autoComplete="new-password" />
          </Field>

          <div className="row" style={{ gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Wird geprüft …' : 'Passwort setzen'}
            </button>
            <button type="button" className="btn" onClick={onCancel} disabled={busy}>
              Zurück zur Anmeldung
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

/**
 * Wird nach der ersten Anmeldung und nach einem Zuruecksetzen durch die
 * Administration erzwungen. Solange das Passwort nicht geaendert ist,
 * verweigert der Server jeden anderen Zugriff.
 */
export function PasswordChangePage() {
  const { refresh, logout } = useAuth();
  const toast = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrors({});

    if (newPassword !== repeatPassword) {
      setErrors({ repeatPassword: 'Die beiden Eingaben stimmen nicht überein.' });
      return;
    }

    setBusy(true);
    try {
      await api.post('/api/auth/password', { currentPassword, newPassword });
      await refresh();
      toast.success('Das Passwort wurde geändert.');
    } catch (caught) {
      if (caught instanceof ApiError) {
        setErrors(
          Object.keys(caught.fieldErrors).length > 0
            ? caught.fieldErrors
            : { _: caught.message },
        );
      } else {
        setErrors({ _: 'Das Passwort konnte nicht geändert werden.' });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="login-title">
          <span className="brand-mark">C</span>
          <h1>Passwort ändern</h1>
        </div>

        <Alert kind="info">
          Bevor Sie weiterarbeiten können, vergeben Sie bitte ein eigenes Passwort. Es muss
          mindestens 10 Zeichen lang sein.
        </Alert>

        {errors._ ? <Alert kind="error">{errors._}</Alert> : null}

        <div className="stack" style={{ gap: 14 }}>
          <Field label="Bisheriges Passwort" htmlFor="current" required error={errors.currentPassword}>
            <TextInput
              id="current"
              type="password"
              value={currentPassword}
              onChange={setCurrentPassword}
              autoFocus
              autoComplete="current-password"
              invalid={Boolean(errors.currentPassword)}
            />
          </Field>

          <Field label="Neues Passwort" htmlFor="new" required error={errors.newPassword}>
            <TextInput
              id="new"
              type="password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              invalid={Boolean(errors.newPassword)}
            />
          </Field>

          <Field
            label="Neues Passwort wiederholen"
            htmlFor="repeat"
            required
            error={errors.repeatPassword}
          >
            <TextInput
              id="repeat"
              type="password"
              value={repeatPassword}
              onChange={setRepeatPassword}
              autoComplete="new-password"
              invalid={Boolean(errors.repeatPassword)}
            />
          </Field>

          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Wird gespeichert …' : 'Passwort ändern'}
          </button>

          <button type="button" className="btn btn-quiet btn-sm" onClick={() => void logout()}>
            Abmelden
          </button>
        </div>
      </form>
    </div>
  );
}
